import { useEffect, useState } from 'react';
import type { PublicShare, PublicShareAsset } from '@xphoto/shared';
import { ShareNotFoundError, fetchShare, fetchShareAssets } from './api';

/**
 * Chargement d'un partage public : l'en-tête, puis TOUTES ses pages de médias, à la
 * suite.
 *
 * ── POURQUOI TOUT CHARGER, PLUTÔT QU'UN CHARGEMENT FENÊTRÉ ────────────────────────
 *
 * La galerie interne a un chargement fenêtré sophistiqué (`page-planner.ts`,
 * virtualisation) parce qu'elle affronte une bibliothèque entière — des dizaines de
 * milliers d'assets, dont l'utilisateur ne verra jamais la fin. Un partage est d'une
 * autre nature : c'est un ALBUM, borné par construction (un dossier qu'on a choisi de
 * montrer, ou au plus 2 000 ids figés). Le charger d'un trait supprime toute la
 * machinerie de fenêtrage, et rend la vue plein écran `/p/:token/:n` correcte à froid
 * pour n'importe quel rang — sans code de « charge jusqu'à l'index N ».
 *
 * Le coût est un DOM proportionnel au partage. Il est tenu par `loading="lazy"` sur
 * chaque vignette : le navigateur ne télécharge que ce qui approche du viewport. Si un
 * partage de plusieurs milliers de médias devenait un usage réel, la virtualisation est
 * l'étape suivante — pas une réécriture, la disposition justifiée est déjà un module pur.
 *
 * Les pages sont annoncées AU FUR ET À MESURE (`assets` grandit) : la grille se peint
 * dès la première, elle n'attend pas la dernière.
 */

export type PublicShareState =
  | { status: 'loading'; assets: PublicShareAsset[] }
  /** Jeton inconnu, révoqué, expiré, ou créateur parti — indiscernables, par conception. */
  | { status: 'not-found' }
  /** Panne réseau ou 5xx : distinct de « n'existe pas », car RÉESSAYER a du sens. */
  | { status: 'error' }
  | { status: 'ready'; share: PublicShare; assets: PublicShareAsset[]; complete: boolean };

export function usePublicShare(token: string): PublicShareState {
  const [state, setState] = useState<PublicShareState>({ status: 'loading', assets: [] });

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setState({ status: 'loading', assets: [] });

    void (async () => {
      try {
        const share = await fetchShare(token, controller.signal);
        if (cancelled) return;

        const collected: PublicShareAsset[] = [];
        let cursor: string | null = null;
        do {
          const page = await fetchShareAssets(token, cursor, controller.signal);
          if (cancelled) return;
          collected.push(...page.items);
          // Garde-fou : une page VIDE assortie d'un curseur suivant boucherait à
          // l'infini. Notre API ne le produit pas (elle n'annonce une suite que si la
          // page était non vide), mais une boucle infinie côté client fige l'onglet —
          // le prix de la garde est nul, celui de son absence ne l'est pas.
          cursor = page.items.length === 0 ? null : page.nextCursor;
          // Publie la progression : la grille se remplit page après page.
          setState({
            status: 'ready',
            share,
            assets: [...collected],
            complete: cursor === null,
          });
        } while (cursor !== null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState(err instanceof ShareNotFoundError ? { status: 'not-found' } : { status: 'error' });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token]);

  return state;
}
